import { Building2, ClipboardList, Mail, MapPin, PackageCheck, Phone } from "lucide-react";
import type { ReactNode } from "react";

import type { CollectionColumn, CollectionRecord } from "../components/CollectionView";
import type { OutboxOperation } from "./offline-outbox";
import { MATERIAL_UNIT_COMPACT_LABELS, MATERIAL_UNIT_LABELS } from "../../shared/material-units";
import type { RouterOutputs } from "./trpc";

type MaterialRequest = RouterOutputs["purchasing"]["requests"]["list"][number];
type Supplier = RouterOutputs["suppliers"]["list"][number];
type SupplierOffer = RouterOutputs["materials"]["listOffers"][number];
type PurchaseOrder = RouterOutputs["purchasing"]["orders"]["list"][number];

const unitLabels = MATERIAL_UNIT_LABELS;

const compactUnitLabels = MATERIAL_UNIT_COMPACT_LABELS;

const quantityNumberFormatter = new Intl.NumberFormat("it-IT", { maximumFractionDigits: 4 });

const requestStatusLabels = {
  requested: "Richiesto",
  ordered: "Ordinato",
  partially_received: "Arrivato in parte",
  received: "Ricevuto",
  used: "Utilizzato",
  cancelled: "Annullato",
} as const;

const orderStatusLabels = {
  draft: "Bozza",
  email_prepared: "Email pronta",
  sent: "Inviato",
  partially_received: "Arrivato in parte",
  received: "Ricevuto",
  cancelled: "Annullato",
} as const;

export const materialRequestColumns: CollectionColumn[] = [
  { key: "data", label: "Data" },
  { key: "autore", label: "Autore" },
  { key: "struttura", label: "Struttura" },
  { key: "quantita", label: "Quantità" },
  { key: "stato", label: "Stato" },
  { key: "ordinato", label: "Ordinato" },
  { key: "ricevuto", label: "Ricevuto" },
  { key: "usato", label: "Usato" },
  { key: "disponibile", label: "Disponibile" },
  { key: "note", label: "Note" },
  { key: "foto", label: "Foto" },
  { key: "sincronizzazione", label: "Sincronizzazione" },
];

export const supplierColumns: CollectionColumn[] = [
  { key: "stato", label: "Stato" },
  { key: "referente", label: "Referente" },
  { key: "email", label: "Email" },
  { key: "telefono", label: "Telefono" },
  { key: "indirizzo", label: "Indirizzo" },
  { key: "note", label: "Note" },
  { key: "offerte", label: "Offerte correnti" },
];

export const purchaseOrderColumns: CollectionColumn[] = [
  { key: "numero", label: "Numero" },
  { key: "data", label: "Data" },
  { key: "fornitore", label: "Fornitore" },
  { key: "strutture", label: "Strutture" },
  { key: "righe", label: "Righe" },
  { key: "quantitaOrdinate", label: "Quantità ordinate" },
  { key: "quantitaRicevute", label: "Quantità ricevute" },
  { key: "totale", label: "Totale" },
  { key: "stato", label: "Stato" },
  { key: "ricezioni", label: "Ricezioni" },
  { key: "note", label: "Note" },
];

function formatDate(value: Date | string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ""
    : new Intl.DateTimeFormat("it-IT", { dateStyle: "medium", timeStyle: "short" }).format(date);
}

function formatShortDate(value: Date | string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ""
    : new Intl.DateTimeFormat("it-IT", { dateStyle: "medium" }).format(date);
}

export function formatQuantity(value: number | string, unit: keyof typeof unitLabels) {
  return `${quantityNumberFormatter.format(Number(value))} ${unitLabels[unit]}`;
}

function formatCompactQuantity(value: number | string, unit: keyof typeof compactUnitLabels) {
  return `${quantityNumberFormatter.format(Number(value))} ${compactUnitLabels[unit]}`;
}

function formatEuros(cents: number) {
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(cents / 100);
}

/** La richiesta materiale come e stata scritta sul dispositivo, letta dal corpo in coda. */
export type QueuedMaterialRequestPayload = {
  siteId: string;
  materialId: string | null;
  freeLabel: string | null;
  requestedQuantity: number;
  unit: keyof typeof unitLabels;
  notes: string | null;
};

function queuedStatus(operation: OutboxOperation) {
  if (operation.status === "blocked") return "Sincronizzazione fallita";
  return "In attesa di sincronizzazione";
}

export function toMaterialRequestRecord(
  request: MaterialRequest,
  index: number,
  actions?: ReactNode,
): CollectionRecord {
  const title = request.materialName || request.freeLabel || "Materiale";
  const status = requestStatusLabels[request.status];

  return {
    id: request.id,
    number: String(index + 1).padStart(2, "0"),
    typeLabel: status,
    icon: <ClipboardList aria-hidden="true" />,
    title,
    subtitle: request.siteName ? <><MapPin aria-hidden="true" />{request.siteName}</> : undefined,
    statusBadge: <span className={`request-status request-status--${request.status}`}>{status}</span>,
    hiddenInCards: ["stato"],
    hiddenInCubes: ["struttura"],
    cells: {
      data: request.createdAt ? { text: formatDate(request.createdAt) } : null,
      autore: request.requestedByName ? { text: request.requestedByName } : null,
      struttura: { text: request.siteName },
      quantita: { text: formatQuantity(request.requestedQuantity, request.unit) },
      stato: { text: status, content: <span className={`request-status request-status--${request.status}`}>{status}</span> },
      ordinato: { text: formatQuantity(request.orderedQuantity ?? 0, request.unit) },
      ricevuto: { text: formatQuantity(request.receivedQuantity ?? 0, request.unit) },
      usato: { text: formatQuantity(request.usedQuantity ?? 0, request.unit) },
      disponibile: { text: formatQuantity(request.availableQuantity ?? 0, request.unit) },
      note: request.notes ? { text: request.notes, expandable: true } : null,
      foto: request.photoObjectKey ? { text: "1 fotografia" } : null,
      sincronizzazione: null,
    },
    actions,
  };
}

export function toQueuedMaterialRequestRecord(
  operation: OutboxOperation,
  payload: QueuedMaterialRequestPayload | null,
  index: number,
  context: { materialName?: string; siteName?: string; requesterName: string; actions: ReactNode },
): CollectionRecord {
  const status = queuedStatus(operation);
  const error = operation.lastError ? `${operation.lastError} · ${operation.attempts} tentativi` : "";

  if (!payload) {
    return {
      id: operation.id,
      number: String(index + 1).padStart(2, "0"),
      typeLabel: status,
      icon: <ClipboardList aria-hidden="true" />,
      title: "Fotografia da sincronizzare",
      cells: {
        data: { text: formatDate(operation.createdAt) },
        autore: { text: context.requesterName },
        struttura: null,
        quantita: null,
        stato: null,
        ordinato: null,
        ricevuto: null,
        usato: null,
        disponibile: null,
        note: error ? { text: error, expandable: true } : null,
        foto: { text: "1 fotografia" },
        sincronizzazione: { text: status },
      },
      actions: context.actions,
    };
  }

  const title = context.materialName || payload.freeLabel || "Materiale";
  return {
    id: operation.id,
    number: String(index + 1).padStart(2, "0"),
    typeLabel: status,
    icon: <ClipboardList aria-hidden="true" />,
    title,
    cells: {
      data: { text: formatDate(operation.createdAt) },
      autore: { text: context.requesterName },
      struttura: { text: context.siteName || "Struttura" },
      quantita: { text: formatQuantity(payload.requestedQuantity, payload.unit) },
      stato: { text: requestStatusLabels.requested },
      ordinato: null,
      ricevuto: null,
      usato: null,
      disponibile: null,
      note: payload.notes || error ? { text: [payload.notes, error].filter(Boolean).join(" · "), expandable: true } : null,
      foto: null,
      sincronizzazione: { text: status },
    },
    actions: context.actions,
  };
}

export function toSupplierRecord(
  supplier: Supplier,
  currentOffers: Array<{ offer: SupplierOffer; materialName: string }>,
  actions?: ReactNode,
  index?: number,
): CollectionRecord {
  const offers = currentOffers.map(({ offer, materialName }) => [
    materialName,
    offer.packageDescription,
    offer.brandVariant,
    offer.packageQuantity == null ? null : `Confezione ${formatCompactQuantity(offer.packageQuantity, offer.packageUnit)}`,
    formatEuros(Number(offer.packagePriceCents)),
    offer.unitPriceCents == null ? null : `${formatEuros(Number(offer.unitPriceCents))} / ${compactUnitLabels[offer.packageUnit]}`,
    offer.notes,
    offer.createdAt ? formatShortDate(offer.createdAt) : null,
  ].filter(Boolean).join(" · ")).join("; ");

  return {
    id: supplier.id,
    number: index === undefined ? undefined : String(index + 1).padStart(2, "0"),
    typeLabel: supplier.active ? "Attivo" : "Inattivo",
    icon: <Building2 aria-hidden="true" />,
    title: supplier.name,
    statusBadge: (
      <span className={`request-status request-status--${supplier.active ? "received" : "cancelled"}`}>
        {supplier.active ? "Attivo" : "Inattivo"}
      </span>
    ),
    hiddenInCards: ["stato"],
    cells: {
      stato: { text: supplier.active ? "Attivo" : "Inattivo" },
      referente: supplier.contactName ? { text: supplier.contactName } : null,
      email: supplier.email ? {
        text: supplier.email,
        content: <a className="collection-contact-link" href={`mailto:${supplier.email}`}><Mail size={15} aria-hidden="true" /> {supplier.email}</a>,
      } : null,
      telefono: supplier.phone ? {
        text: supplier.phone,
        content: <a className="collection-contact-link" href={`tel:${supplier.phone}`}><Phone size={15} aria-hidden="true" /> {supplier.phone}</a>,
      } : null,
      indirizzo: supplier.address ? { text: supplier.address } : null,
      note: supplier.notes ? { text: supplier.notes, expandable: true } : null,
      offerte: offers ? { text: offers, expandable: true } : null,
    },
    actions,
  };
}

function receiptLabel(order: PurchaseOrder) {
  const lines = order.lines ?? [];
  if (lines.every((line) => Number(line.receivedQuantity) === 0)) return "Nessuna ricezione";
  if (lines.length > 0 && lines.every((line) => Number(line.receivedQuantity) >= Number(line.orderedQuantity))) {
    return "Ricezione completata";
  }
  return "Ricezioni parziali";
}

function quantityTotalsByUnit(
  lines: PurchaseOrder["lines"],
  quantity: "orderedQuantity" | "receivedQuantity",
) {
  const totals = new Map<keyof typeof compactUnitLabels, number>();
  for (const line of lines ?? []) {
    totals.set(line.unit, (totals.get(line.unit) ?? 0) + Number(line[quantity]));
  }
  return Array.from(totals, ([unit, total]) => formatCompactQuantity(total, unit)).join(" · ");
}

export function toPurchaseOrderRecord(order: PurchaseOrder): CollectionRecord {
  const orderStatus = orderStatusLabels[order.status];
  const receipts = receiptLabel(order);
  const lines = (order.lines ?? []).map((line) => (
    `${line.label}, ${formatCompactQuantity(line.orderedQuantity, line.unit)}, ricevuti ${formatCompactQuantity(line.receivedQuantity, line.unit)}`
  )).join("; ");
  const orderedQuantities = quantityTotalsByUnit(order.lines, "orderedQuantity");
  const receivedQuantities = quantityTotalsByUnit(order.lines, "receivedQuantity");
  const title = `Ordine #${order.orderNumber}`;

  return {
    id: order.id,
    number: `#${order.orderNumber}`,
    typeLabel: orderStatus,
    icon: <PackageCheck aria-hidden="true" />,
    title,
    subtitle: order.supplierName ? <><Building2 aria-hidden="true" />{order.supplierName}</> : undefined,
    statusBadge: <span className={`request-status request-status--${order.status}`}>{orderStatus}</span>,
    hiddenInCards: ["stato"],
    hiddenInCubes: ["fornitore"],
    detailHref: `/acquisti/ordini/${order.id}`,
    cells: {
      numero: { text: `#${order.orderNumber}` },
      data: { text: formatDate(order.createdAt) },
      fornitore: { text: order.supplierName },
      strutture: (order.siteNames?.length ?? 0) > 0 ? { text: order.siteNames.join(", ") } : null,
      righe: lines ? { text: lines, expandable: lines.length > 120 } : null,
      quantitaOrdinate: orderedQuantities ? { text: orderedQuantities } : null,
      quantitaRicevute: receivedQuantities ? { text: receivedQuantities } : null,
      totale: order.totalPriceCents == null ? null : { text: formatEuros(order.totalPriceCents) },
      stato: {
        text: orderStatus,
        content: <span className={`request-status request-status--${order.status}`}>{orderStatus}</span>,
      },
      ricezioni: { text: receipts },
      note: order.notes ? { text: order.notes, expandable: order.notes.length > 120 } : null,
    },
  };
}

export function collectionRecordSearchValues(record: CollectionRecord) {
  return [record.title, record.number, record.typeLabel, ...Object.values(record.cells).map((cell) => cell?.text)];
}
